export const COLORS = {
  weekend: "#C4BD97",
  holiday: "#8DB4E2",
  exception: "#494529",
  work_on_holiday: "#16365C",
  unplanned: "#FFFFFF",
  on_time: "#b7e4c7",
  delivery_delay: "#FFFF00",
  delivery_early: "#0070C0",
  general_duty: "transparent",
  ot_upto_2h: "transparent",
  ot_more_than_2h: "transparent",
  learning_curve_applied: "red",
  // Calendar specific colors
  past_date: "#d9d9d9",
  past_full_day_planned: "#faad14", // Light green for past fully planned days
  full_day_planned: "#E3F2FD",
  drop_active: "#bae637",
  dropping: "#52c41a",
  hover_color: "rgb(87, 207, 255)",
};

export const PLAN_COLORS = {
  0: "#2f54eb", // indigo
  1: "#faad14", // yellow
  2: "#52c41a", // green
  3: "#1890ff", // blue
  4: "#722ed1", // purple
  5: "#eb2f96", // pink
  6: "#13c2c2", // cyan
  7: "#fa8c16", // orange
  8: "#f5222d", // red
  9: "#8c8c8c", // gray
  10: "#ff7a45", // volcano
  11: "#fadb14", // gold
};

export const GRID_CONFIG = {
  DAY_WITH_DUTY_COLUMNS: 1,
  LINE_LIST_COLUMNS: 2,
  PLAN_GRID_COLUMNS: 3,
};

// ==================== UTILITY FUNCTIONS ====================
export const dateUtils = {
  getCurrentDateMidnight: () => {
    const today = new Date();
    return today.setHours(0, 0, 0, 0);
  },

  getDateMidnight: (dateString) => {
    return new Date(dateString).setHours(0, 0, 0, 0);
  },

  isPastDate: (dateString) => {
    const currentDate = dateUtils.getCurrentDateMidnight();
    const dateValue = dateUtils.getDateMidnight(dateString);
    return dateValue < currentDate;
  },

  isFutureOrToday: (dateString) => {
    return !dateUtils.isPastDate(dateString);
  },
};

export const planUtils = {
  isDraggable: (dateObj) => {
    return dateUtils.isFutureOrToday(dateObj.date) && dateObj.plans && dateObj.plans.length > 0;
  },

  hasPlans: (dateObj) => {
    return dateObj.plans && dateObj.plans.length > 0;
  },

  getStartPlans: (plans) => {
    return plans.filter((plan) => plan.is_start);
  },
};

// ==================== STYLE GENERATORS ====================
export const styleGenerators = {
  getCalendarMainStyle: (days, linesCount) => ({
    display: "grid",
    gridTemplateColumns: `repeat(${days}, 1fr)`,
    gridTemplateRows: `repeat(${linesCount + 2}, auto)`,
    gap: "5px",
    width: "100%",
  }),

  getProductionListStyle: (lineListColumns) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#D5EEFF",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "5px",
    gridRow: "span 3",
    gridColumn: `span ${lineListColumns}`,
    fontWeight: "bold",
  }),

  getHeaderYearMonthStyle: (days, lineListColumns) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#D5EEFF",
    padding: "10px",
    fontSize: "16px",
    borderRadius: "5px",
    gridColumn: `span ${days - lineListColumns}`,
    fontWeight: "bold",
  }),

  getHeaderDayStyle: (dayWithDutyColumns, day) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: "1px solid #ccc",
    padding: "10px",
    fontSize: "14px",
    borderRadius: "5px",
    gridColumn: `span ${dayWithDutyColumns}`,
    backgroundColor: day.week_status ? COLORS[day.week_status] : "#f1f1f1",
    color: day.week_status === "exception" ? "white" : "black",
  }),

  getDateCellStyle: (dayWithDutyColumns, dateObj, isDropActive, isDropping) => {
    let bgColor = COLORS.unplanned;

    // Exception status should override ALL other colors (highest priority)
    if (dateObj.week_status === "exception") {
      bgColor = COLORS.exception;
    } else if (dateObj.week_status === "holiday") {
      // Holiday status should override other colors (except exception)
      bgColor = COLORS.holiday;
    } else if (dateObj.week_status === "weekend") {
      // Weekend status should override other colors (except exception and holiday)
      bgColor = COLORS.weekend;
    } else {
      // Only apply other colors if not an exception, holiday, or weekend
      if (isDropping) {
        bgColor = COLORS.dropping;
      } else if (isDropActive) {
        bgColor = COLORS.drop_active;
      } else if (dateObj.is_full_day_planned) {
        bgColor = COLORS.full_day_planned;
      } else if (dateObj.week_status) {
        bgColor = COLORS[dateObj.week_status] || COLORS.unplanned;
      }

      // Override for past dates (but not if it's an exception, holiday, or weekend)
      if (dateUtils.isPastDate(dateObj.date)) {
        // Weekend color is already set above, so keep it
        if (dateObj.week_status === "weekend") {
          // Keep weekend color for past weekends (already set above)
          // bgColor remains COLORS.weekend
        } else if (dateObj.is_full_day_planned) {
          // Special color for past fully planned days
          bgColor = COLORS.past_full_day_planned;
        } else {
          // Regular past date color
          bgColor = COLORS.past_date;
        }
      } else {
        if (!dateObj?.is_full_day_planned) {
          console.log("No full day plans for date:", dateObj);
          const totalMH = dateObj.plans.reduce((sum, plan) => sum + (Number(plan.plan_curve_mh).toFixed(2) || 0), 0);
          if (totalMH > 0 && totalMH < 10) {
            bgColor = COLORS.on_time;
          }
        }
      }
    }

    // Determine cursor type based on date and status
    let cursorType = "pointer";
    if (
      dateUtils.isPastDate(dateObj.date) ||
      dateObj.week_status === "weekend" ||
      dateObj.week_status === "holiday" ||
      dateObj.is_full_day_planned
    ) {
      cursorType = "not-allowed";
    } else if (dateObj.week_status === "unplanned") {
      cursorType = "pointer"; // Make it clear empty cells are clickable
    } else {
      cursorType = "move"; // For cells with plans
    }

    return {
      display: "grid",
      gridTemplateColumns: `repeat(${GRID_CONFIG.PLAN_GRID_COLUMNS}, 1fr)`,
      padding: "5px",
      justifyContent: "evenly",
      gap: "2px",
      width: "100%",
      border: "1px solid #cccccc",
      backgroundColor: bgColor,
      color: dateObj.is_apply_learning_curve ? COLORS.learning_curve_applied : "black",
      fontSize: "14px",
      borderRadius: "5px",
      gridColumn: `span ${dayWithDutyColumns}`,
      cursor: cursorType,
      transition: "background-color 0.1s ease",
      textTransform: "capitalize",
      position: "relative",
    };
  },

  getPlanDotStyle: (planIndex, isDropping, isHovered) => ({
    backgroundColor: isDropping
      ? COLORS.dropping
      : isHovered
      ? COLORS.hover_color
      : PLAN_COLORS[planIndex % Object.keys(PLAN_COLORS).length],
    display: "flex",
    borderRadius: "50%",
    width: "12px",
    height: "12px",
    border: "1px solid #fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
    cursor: isDropping ? "wait" : "pointer",
    transition: "all 0.2s ease",
    opacity: isDropping ? 0.7 : 1,
  }),
};
